import { ApolloError } from "@apollo/client";
import React, {
    ReactElement,
} from "react";
import useRouting, { Paths } from "../../contexts/Routing";
import Styles from "./Error.module.scss";

const ERROR_IMAGE = "https://static.foqal.io/agent/failbot.png";
const SECURITY_IMAGE = ERROR_IMAGE;

interface ErrorProps {
    message: string;
    title?: string;
    stacktrace?: string[] | undefined;
    image?: string;
    shouldShowLogin?: boolean;
    className?: string;
}

export function ErrorView(props: ErrorProps): ReactElement {
    const { createHistoryPush } = useRouting();
    return (
        <div className={`d-flex align-items-center w-100 ${props.className}`}>
            <img src={props.image ?? ERROR_IMAGE} alt="error" className={`me-2 ${Styles["error-image"]}`} />
            <div className="flex-grow-1 overflow-auto">
                <h3 className="mb-4">{props.title}</h3>
                <div className="font-light mb-3">{props.message}</div>
                {props.stacktrace && (
                    <div className="alert alert-secondary">
                        <pre className="text-start">
                            <code>
                                {props.stacktrace.join("\n")}
                            </code>
                        </pre>
                    </div>
                )}
                {props.shouldShowLogin && (
                    <button type="button" onClick={createHistoryPush(Paths.LOGIN)} className="btn btn-primary px-4 btn">
                        Login
                    </button>
                )}
            </div>
        </div>
    );
}

ErrorView.defaultProps = {
    title: "Oops",
    stacktrace: undefined,
    image: ERROR_IMAGE,
    shouldShowLogin: false,
    className: "",
};

export function ErrorDefault(props: ErrorProps & {pageClassName?: string}): ReactElement {
    return (
        <div className="full-page">
            <div className={`centered-container${props.stacktrace ? "-lg" : ""} shadow-lg border text-center ${
                props.pageClassName ?? "p-4"
            }`}
            >
                <ErrorView {...props} />
            </div>
        </div>
    );
}
ErrorDefault.defaultProps = {
    title: "Oops",
    stacktrace: undefined,
    image: ERROR_IMAGE,
    shouldShowLogin: false,
    className: "",
    pageClassName: "",
};

export function Error404(): ReactElement {
    return (
        <ErrorDefault
            message="We have no idea how you are getting this page, but we don&apos;t know where it is either."
        />
    );
}

export function ErrorSecurity({ message, title }: {message: string, title?: string}): ReactElement {
    return (
        <ErrorDefault
            title={title}
            image={SECURITY_IMAGE}
            message={message}
            shouldShowLogin
        />
    );
}
ErrorSecurity.defaultProps = {
    title: "Not Authorized",
};

export function ErrorRemote({ error }: {error?: ApolloError}): ReactElement {
    const firstError = error?.graphQLErrors?.[0];
    const firstException = firstError?.extensions?.exception as Error & ({stacktrace: string[]}) | undefined;
    return (
        <ErrorDefault
            title={firstError?.message ?? "Internal Error"}
            message={firstException?.name ?? error?.message ?? "Error downloading from the server."}
            stacktrace={firstException?.stacktrace}
        />
    );
}
ErrorRemote.defaultProps = {
    error: undefined,
};

export function ErrorRemoteView({ error }: {error?: ApolloError}): ReactElement {
    const firstError = error?.graphQLErrors?.[0];
    const firstException = firstError?.extensions?.exception as Error & ({stacktrace: string[]}) | undefined;

    return (
        <ErrorView
            title={firstError?.message ?? "Internal Error"}
            message={firstException?.name ?? error?.message ?? "Error downloading from the server."}
            stacktrace={firstException?.stacktrace}
        />
    );
}
ErrorRemoteView.defaultProps = {
    error: undefined,
};

export function NoDataOrError({ error }: {error?: ApolloError}): ReactElement {
    return error ? <ErrorRemote error={error} /> : (
        <ErrorDefault
            title="Error Connecting"
            message="No data returned."
        />
    );
}
NoDataOrError.defaultProps = {
    error: undefined,
};

export function NoDataOrErrorView({ error }: {error?: ApolloError}): ReactElement {
    return error ? <ErrorRemoteView error={error} /> : (
        <ErrorView
            title="Error Connecting"
            message="No data returned."
        />
    );
}
NoDataOrErrorView.defaultProps = {
    error: undefined,
};
